import * as React from 'react';
import { LexicalTypeaheadMenuPlugin, MenuOption, useBasicTypeaheadTriggerMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, $getSelection, $isRangeSelection, type TextNode } from 'lexical';
import { createPortal } from 'react-dom';
import styles from '../lexical-editor.module.css';

type EmojiDefinition = {
  emoji: string;
  name: string;
  keywords: string[];
};

const EMOJI_LIST: EmojiDefinition[] = [
  { emoji: '😀', name: 'grinning', keywords: ['face', 'happy', 'smile'] },
  { emoji: '😁', name: 'beaming', keywords: ['face', 'happy', 'smile'] },
  { emoji: '😂', name: 'joy', keywords: ['face', 'funny', 'laugh'] },
  { emoji: '🤣', name: 'rofl', keywords: ['face', 'funny', 'laugh'] },
  { emoji: '😊', name: 'blush', keywords: ['face', 'happy', 'smile'] },
  { emoji: '😍', name: 'heart-eyes', keywords: ['love', 'face', 'crush'] },
  { emoji: '😘', name: 'kiss', keywords: ['face', 'love'] },
  { emoji: '😎', name: 'cool', keywords: ['face', 'sunglasses'] },
  { emoji: '🤗', name: 'hug', keywords: ['face', 'care', 'support'] },
  { emoji: '🤔', name: 'thinking', keywords: ['face', 'hmm', 'question'] },
  { emoji: '🙌', name: 'raised-hands', keywords: ['celebration', 'hands'] },
  { emoji: '👏', name: 'clap', keywords: ['applause', 'hands'] },
  { emoji: '👍', name: 'thumbs-up', keywords: ['approve', 'like', 'yes'] },
  { emoji: '👎', name: 'thumbs-down', keywords: ['dislike', 'no'] },
  { emoji: '👋', name: 'wave', keywords: ['hello', 'goodbye', 'hands'] },
  { emoji: '🙏', name: 'pray', keywords: ['thanks', 'please', 'hands'] },
  { emoji: '🔥', name: 'fire', keywords: ['hot', 'lit', 'trend'] },
  { emoji: '✨', name: 'sparkles', keywords: ['magic', 'shine'] },
  { emoji: '⭐', name: 'star', keywords: ['favorite', 'highlight'] },
  { emoji: '💫', name: 'dizzy', keywords: ['sparkle', 'magic'] },
  { emoji: '❤️', name: 'heart', keywords: ['love', 'like'] },
  { emoji: '🧡', name: 'orange-heart', keywords: ['love', 'heart'] },
  { emoji: '💛', name: 'yellow-heart', keywords: ['love', 'heart'] },
  { emoji: '💚', name: 'green-heart', keywords: ['love', 'heart'] },
  { emoji: '💙', name: 'blue-heart', keywords: ['love', 'heart'] },
  { emoji: '💜', name: 'purple-heart', keywords: ['love', 'heart'] },
  { emoji: '🤍', name: 'white-heart', keywords: ['love', 'heart'] },
  { emoji: '🖤', name: 'black-heart', keywords: ['love', 'heart'] },
  { emoji: '💔', name: 'broken-heart', keywords: ['sad', 'heart'] },
  { emoji: '🎉', name: 'tada', keywords: ['party', 'celebration'] },
  { emoji: '🎊', name: 'confetti', keywords: ['party', 'celebration'] },
  { emoji: '🥳', name: 'partying', keywords: ['party', 'celebration'] },
  { emoji: '✅', name: 'check', keywords: ['done', 'yes', 'success'] },
  { emoji: '❌', name: 'cross', keywords: ['no', 'error', 'cancel'] },
  { emoji: '⚠️', name: 'warning', keywords: ['alert', 'attention'] },
  { emoji: '🚀', name: 'rocket', keywords: ['launch', 'ship', 'growth'] },
  { emoji: '💡', name: 'bulb', keywords: ['idea', 'tip'] },
  { emoji: '📌', name: 'pin', keywords: ['important', 'note'] },
  { emoji: '📣', name: 'megaphone', keywords: ['announcement', 'notice'] },
  { emoji: '📅', name: 'calendar', keywords: ['date', 'schedule'] },
  { emoji: '⏰', name: 'alarm', keywords: ['time', 'reminder'] },
  { emoji: '🎁', name: 'gift', keywords: ['present', 'reward'] },
  { emoji: '🏆', name: 'trophy', keywords: ['win', 'award'] },
  { emoji: '👀', name: 'eyes', keywords: ['look', 'watch'] },
  { emoji: '💯', name: '100', keywords: ['perfect', 'score'] },
  { emoji: '🤝', name: 'handshake', keywords: ['deal', 'agreement'] },
  { emoji: '📈', name: 'chart-up', keywords: ['growth', 'stats'] },
  { emoji: '📉', name: 'chart-down', keywords: ['decline', 'stats'] },
  { emoji: '💬', name: 'speech-balloon', keywords: ['comment', 'chat'] },
  { emoji: '📝', name: 'memo', keywords: ['note', 'write'] },
];

class EmojiOption extends MenuOption {
  emoji: string;
  keywords: string[];
  title: string;

  constructor({ emoji, keywords, name }: EmojiDefinition) {
    super(name);
    this.emoji = emoji;
    this.keywords = keywords;
    this.title = name;
  }
}

interface EmojiPickerPluginProps {
  disabled?: boolean;
}

export function EmojiPickerPlugin({
  disabled = false,
}: EmojiPickerPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = React.useState<string | null>(null);
  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch(':', {
    minLength: 0,
    maxLength: 75,
  });
  const options = React.useMemo(() => {
    const normalizedQuery = queryString?.toLowerCase().trim() ?? '';

    return EMOJI_LIST
      .filter(({ keywords, name }) => {
        if (normalizedQuery === '') {
          return true;
        }

        return (
          name.includes(normalizedQuery) ||
          keywords.some((keyword) => keyword.includes(normalizedQuery))
        );
      })
      .slice(0, 8)
      .map((emoji) => new EmojiOption(emoji));
  }, [queryString]);

  const onSelectOption = React.useCallback(
    (
      selectedOption: EmojiOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        const emojiNode = $createTextNode(selectedOption.emoji);

        if (nodeToReplace) {
          nodeToReplace.replace(emojiNode);
        } else {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            selection.insertNodes([emojiNode]);
          }
        }

        emojiNode.selectNext();
        closeMenu();
      });
    },
    [editor]
  );

  if (disabled) {
    return null;
  }

  return (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      options={options}
      triggerFn={checkForTriggerMatch}
      menuRenderFn={(anchorElementRef, { options, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (!anchorElementRef.current || options.length === 0) {
          return null;
        }

        return createPortal(
          <div className={styles.emojiMenu}>
            <ul className={styles.emojiMenuList}>
              {options.map((option, index) => (
                <li key={option.key}>
                  <button
                    className={styles.emojiMenuItem}
                    data-selected={selectedIndex === index}
                    onClick={() => {
                      selectOptionAndCleanUp(option);
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onMouseEnter={() => {
                      setHighlightedIndex(index);
                    }}
                    type="button"
                  >
                    <span className={styles.emojiMenuGlyph}>{option.emoji}</span>
                    <span className={styles.emojiMenuName}>
                      :{option.title}:
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          anchorElementRef.current
        );
      }}
    />
  );
}
